<script lang="ts">
    import PanelistSurveyAlternativePage from '@Evaluation/AlternativesSurveys/Pages/PanelistSurveyAlternativePage'
    export default PanelistSurveyAlternativePage
</script>

<template>
    <div>
        <CriterionDetailsDialog :viewModel="criterionDetailsDialog" />

        <AlternativeDetailsDialog :viewModel="alternativeDetailsDialog" />

        <div class="container">
            <h3 class="text-center" v-if="initialized === false"><i class="fa fa-spinner fa-spin"></i> Loading...</h3>

            <template v-if="initialized === true">
                <nav class="navbar fixed-bottom navbar-dark bg-dark">
                    <div class="container d-flex justify-content-center">
                        <div class="btn-group" role="group">
                            <a class="btn btn-secondary" v-sanitizedHref="`/#/scoring/alternative/${workspaceId}/${surveyId}/${projectUserId}/${prevAlternativeId}`">
                                <i class="fa fa-angle-double-left" aria-hidden="true"></i>
                                Prev <span class="text-lowercase d-none d-sm-inline">{{alternativeLabel}}</span>
                            </a>
                            <button type="button" class="btn btn-warning" @click="scrollTop">
                                Back to Top
                            </button>
                            <a class="btn btn-secondary" v-sanitizedHref="`/#/scoring/alternative/${workspaceId}/${surveyId}/${projectUserId}/${nextAlternativeId}`">
                                Next <span class="text-lowercase d-none d-sm-inline">{{alternativeLabel}}</span>
                                <i class="fa fa-angle-double-right" aria-hidden="true"></i>
                            </a>
                        </div>
                    </div>
                </nav>

                <nav class="navbar navbar-expand-md navbar-dark bg-dark fixed-top">
                    <div class="container">

                        <a class="btn btn-light mr-auto" v-sanitizedHref="`/#/scoring/dashboard/${workspaceId}/${surveyId}/${projectUserId}`">
                            <i class="fa fa-angle-double-left" aria-hidden="true"></i>
                            My Survey
                        </a>

                        <div class="progress mx-2 w-50" style="height: 38px; background-color: #D3D3D3;">
                            <div class="progress-bar" role="progressbar" :style="{'width': progress + '%'}">{{AnsweredQuestionsCount}} / {{questionsCount}}</div>
                        </div>

                        <button v-if="IsSaved == null" type="button" class="btn btn-warning" @click="handleSavePanelistVotesCommand">
                            <i class="fa fa-spinner fa-spin"></i> Saving...
                        </button>
                        <button v-if="IsSaved === true" type="button" class="btn btn-light" @click="handleSavePanelistVotesCommand">
                            Saved
                        </button>
                        <button v-if="IsSaved === false" type="button" class="btn btn-warning" @click="handleSavePanelistVotesCommand">
                            Save
                        </button>
                    </div>
                </nav>


                <div style="padding-top: 80px; padding-bottom: 120px;">
                    <h3 id="top" class="mt-4">
                        <span class="font-weight-light">Evaluate <span class="text-lowercase">{{alternativeLabel}}</span>: </span> {{alternativeName}}
                    </h3>
                    <hr />

                    <nav class="navbar navbar-expand-md navbar-light bg-light">
                        <ul class="navbar-nav">
                            <li class="nav-item dropdown">
                                <a class="nav-link dropdown-toggle" id="navbarDropdown" role="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                    Questions: {{QuestionsFilter ?? 'All'}}
                                </a>
                                <div class="dropdown-menu">
                                    <a class="dropdown-item" @click="QuestionsFilter = null">All <span class="badge badge-secondary">{{questionsCount}}</span></a>
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item" @click="QuestionsFilter = 'Answered'">Answered <span class="badge badge-secondary">{{AnsweredQuestionsCount}}</span></a>
                                    <a class="dropdown-item" @click="QuestionsFilter = 'Not answered'">Not answered <span class="badge badge-secondary">{{NotAnsweredQuestionsCount}}</span></a>
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item" @click="QuestionsFilter = 'Commented'">Commented <span class="badge badge-secondary">{{CommentedQuestionsCount}}</span></a>
                                    <a class="dropdown-item" @click="QuestionsFilter = 'Not commented'">Not commented <span class="badge badge-secondary">{{NotCommentedQuestionsCount}}</span></a>
                                </div>
                            </li>
                        </ul>
                        <form class="form-inline my-2 my-lg-0 ml-auto">
                            <input class="form-control mr-sm-2" type="search" placeholder="Filter criteria" v-model="SearchPhrase">
                        </form>
                    </nav>


                    <div v-for="dr in DirectRatesView" :key="dr.Id">
                        <p class="mt-5 mb-3 lead">
                            <strong>{{dr.OrderValue}}.</strong>
                            How would you rate <span class="text-lowercase">{{alternativeLabel}}</span>
                            <a class="text-dark font-italic" @click="ShowAlternativeDetailsCommand(alternativeName, alternativeDescription)">{{alternativeName}}</a>
                            in the context of <a class="text-primary" @click="ShowCriterionDetailsCommand(dr)">{{dr.CriterionName}}</a>?
                        </p>

                        <div class="row">
                            <div class="col-md-6">
                                <PanelistSurveyVoteEditorDr :propValue="dr.Value"
                                                            :propComment="dr.Comment"
                                                            :propScaleLevels="dr.ScaleLevels"
                                                            @update-value="updateValue(dr, $event)"
                                                            @update-comment="updateComment(dr, $event)" />
                            </div>
                            <div class="col-md-6">
                                <h5>Criterion description</h5>
                                <p class="text-muted">{{dr.CriterionPath}}</p>
                                <p v-if="!dr.CriterionDescription" class="text-muted font-italic">No description provided</p>
                                <p v-html="dr.CriterionDescription"></p>
                            </div>
                        </div>
                    </div>

                    <div v-for="pc in PairwiseComparisonsView" :key="pc.Id">
                        <p class="mt-5 mb-3 lead">
                            <strong>{{pc.OrderValue}}.</strong>
                            In the context of <a class="text-primary" @click="ShowCriterionDetailsCommand(pc)">{{pc.CriterionName}}</a>,
                            which <span class="text-lowercase">{{alternativeLabel}}</span> do you prefer: <br />
                            <a class="text-dark font-italic" @click="ShowAlternativeDetailsCommand(pc.FirstAlternativeName, pc.FirstAlternativeDescription)">{{pc.FirstAlternativeName}}</a> or
                            <a class="text-primary" @click="ShowAlternativeDetailsCommand(pc.SecondAlternativeName, pc.SecondAlternativeDescription)">{{pc.SecondAlternativeName}}</a>?
                        </p>

                        <div class="row">
                            <div class="col-md-6">
                                <PanelistSurveyVoteEditorPc :propValue="pc.Value"
                                                            :propComment="pc.Comment"
                                                            :propFirstAlternativeName="pc.FirstAlternativeName"
                                                            :propSecondAlternativeName="pc.SecondAlternativeName"
                                                            @update-value="updateValue(pc, $event)"
                                                            @update-comment="updateComment(pc, $event)" />
                            </div>
                            <div class="col-md-6">
                                <h5>Criterion description</h5>
                                <p class="text-muted">{{pc.CriterionPath}}</p>
                                <p v-if="!pc.CriterionDescription" class="text-muted font-italic">No description provided</p>
                                <p v-html="pc.CriterionDescription"></p>
                            </div>
                        </div>
                    </div>

                </div>
            </template>
        </div>
    </div>
</template>
